import { Collapse, Dialog, LinearProgress } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Link from '../common/util/input/Link'
import { DropAlert } from './DropAlert'
import { DropPaper } from './DropPaper'
import { useSharedState } from '../waxplorer/Waxplorer'
import MainButton from '../common/util/input/MainButton'

export const DropAuthDialog = ({
    open,
    setOpen,
    drop,
    isAuthRequired,
    setIsAuthRequired,
}) => {
    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const [isUpdating, setIsUpdating] = useState(false)

    const [error, setError] = useState(null)
    const [transactionId, setTransactionId] = useState(null)

    const { t } = useTranslation('common')

    const updateDropAuth = async () => {
        setIsUpdating(true)

        const actions = []

        const data = {
            authorized_account: userName,
            drop_id: parseInt(drop.dropId),
            auth_required: !isAuthRequired,
        }

        const action = {
            account: 'nfthivedrops',
            name: 'setdropauth',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: data,
        }

        actions.push(action)

        let transactionId = null

        try {
            const result = await activeUser.session.transact(
                {
                    actions: actions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )

            transactionId = result?.response?.transaction_id
        } catch (e) {
            setError(e.message)
        }
        setIsAuthRequired(!isAuthRequired)
        setIsUpdating(false)
        setTransactionId(transactionId)
    }

    const close = () => {
        if (!isUpdating) {
            setOpen(false)
            setError(null)
            setTransactionId(null)
        }
    }

    return (
        <Dialog open={open} onClose={() => close()} maxWidth="sm" fullWidth>
            <DropPaper
                noMargin
                title={
                    transactionId
                        ? isAuthRequired
                            ? t('creator.auth_enabled')
                            : t('creator.auth_disabled')
                        : isAuthRequired
                        ? t('creator.do_you_want_disable_user_verification')
                        : t('creator.do_you_want_enable_user_verification')
                }
            >
                <div className="mb-10">
                    {isAuthRequired
                        ? 'You will need to update the settings afterwards!'
                        : 'Any user will be able to purchase this drop once this setting is disabled!'}
                </div>
                <DropAlert condition={error} severity="error" className="mb-5">
                    {error}
                </DropAlert>
                <DropAlert
                    condition={transactionId}
                    transactionId={transactionId}
                    title="Success!"
                    className="mb-5"
                >
                    {isAuthRequired ? (
                        <Link
                            href={`creator/${drop?.collection}/dropcreator/${drop?.dropId}/auth`}
                            className="text-primary font-bold hover:underline"
                        >
                            Click here to update the user verification settings
                        </Link>
                    ) : (
                        <Link
                            href={`/creator/${drop?.collection}/dropcreator`}
                            className="text-primary font-bold hover:underline"
                        >
                            Click here to go back to the overview
                        </Link>
                    )}
                </DropAlert>
                <Collapse in={isUpdating}>
                    <div className="mb-10">
                        <LinearProgress />
                    </div>
                </Collapse>
                {transactionId ? (
                    <div className="flex justify-end">
                        <MainButton
                            onClick={() => close()}
                            disabled={isUpdating}
                            className="sm:mr-5"
                        >
                            {' '}
                            {t('buttons.close')}
                        </MainButton>
                    </div>
                ) : (
                    <div className="flex justify-end">
                        <div className="sm:mr-5">
                            <MainButton
                                color="danger"
                                onClick={() => close()}
                                disabled={
                                    isUpdating ||
                                    (!isAuthRequired && transactionId)
                                }
                                className="sm:mr-5"
                            >
                                {t('buttons.cancel')}
                            </MainButton>
                        </div>
                        <div className="sm:mr-5">
                            <MainButton
                                onClick={() => updateDropAuth()}
                                disabled={isUpdating || transactionId}
                            >
                                {isAuthRequired
                                    ? t('buttons.disable_user_verification')
                                    : t('buttons.enable_user_verification')}
                            </MainButton>
                        </div>
                    </div>
                )}
            </DropPaper>
        </Dialog>
    )
}
